import { ReactNode } from "react";
import { ArrowRight } from "lucide-react";
import { cn } from "web-design-system";
import {
  AllocationBucket,
  PortfolioReviewInsights,
  ReviewExposureSnapshot,
} from "creso-core/types";
import { formatAsPercentage, formatShort } from "creso-core/utils";
import { ALLOCATION_COLORS } from "../../constants";

const formatPct = (value: string | number) => formatAsPercentage(value, "-", 1);

export function LiveInsights({
  insights,
}: {
  insights: PortfolioReviewInsights;
}) {
  return (
    <aside className="flex flex-col gap-4 rounded-2xl bg-card p-4">
      <div className="flex flex-col gap-3">
        <span className="text-xs font-semibold text-muted-foreground">
          Live insights
        </span>
        <div className="flex gap-6 text-xs text-muted-foreground">
          <LegendItem>Current portfolio</LegendItem>
          <LegendItem dashed>After review</LegendItem>
        </div>
      </div>

      <InsightCard className="grid grid-cols-4 gap-3">
        <Stat label="Amount moved" value={formatShort(insights.amount_moved)} />
        <Stat label="STCG" value={formatShort(insights.tax.stcg_tax)} />
        <Stat label="LTCG" value={formatShort(insights.tax.ltcg_tax)} />
        <Stat label="Total tax" value={formatShort(insights.tax.total_tax)} />
      </InsightCard>

      <InsightCard title="Asset Allocation">
        <AllocationComparison
          before={insights.asset_allocation.before}
          after={insights.asset_allocation.after}
        />
      </InsightCard>

      <InsightCard title="Exposure">
        <ExposureComparison
          before={insights.exposure.before}
          after={insights.exposure.after}
        />
      </InsightCard>
    </aside>
  );
}

function InsightCard({
  title,
  className,
  children,
}: {
  title?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section
      className={cn(
        "flex flex-col gap-6 rounded-lg border border-border bg-background p-4",
        className
      )}
    >
      {title ? <h6>{title}</h6> : null}
      {children}
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-semibold">{value}</span>
    </div>
  );
}

function LegendItem({
  dashed,
  children,
}: {
  dashed?: boolean;
  children: ReactNode;
}) {
  return (
    <span className="flex items-center gap-2">
      <span
        className={cn(
          "w-5 border-t-4 border-muted-foreground",
          dashed && "border-dotted"
        )}
      />
      {children}
    </span>
  );
}

// Solid bar for the current portfolio, dashed for after the review.
function InsightBar({
  pct,
  color,
  dashed,
}: {
  pct: number;
  color: string;
  dashed?: boolean;
}) {
  return (
    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
      <div
        className="h-full rounded-full"
        style={{
          width: `${Math.min(Math.max(pct, 0), 100)}%`,
          background: dashed
            ? `repeating-linear-gradient(90deg, ${color} 0 6px, transparent 6px 10px)`
            : color,
        }}
      />
    </div>
  );
}

function BarPair({
  before,
  after,
  color,
}: {
  before: number;
  after: number;
  color: string;
}) {
  return (
    <div className="flex flex-1 flex-col gap-1">
      <div className="flex h-5 items-center gap-4">
        <InsightBar pct={before} color={color} />
        <span className="w-12 text-right text-xs">{formatPct(before)}</span>
      </div>
      <div className="flex items-center h-5 gap-4">
        <InsightBar pct={after} color={color} dashed />
        <span className="w-12 text-right text-xs">{formatPct(after)}</span>
      </div>
    </div>
  );
}

type MergedBucket = {
  key: string;
  label: string;
  before: number;
  after: number;
};

function mergeBuckets(
  before: { key: string; label: string; pct: number }[],
  after: { key: string; label: string; pct: number }[]
): MergedBucket[] {
  const merged = new Map<string, MergedBucket>();
  before.forEach((b) =>
    merged.set(b.key, { key: b.key, label: b.label, before: b.pct, after: 0 })
  );
  after.forEach((b) => {
    const existing = merged.get(b.key);
    merged.set(b.key, {
      key: b.key,
      label: existing?.label ?? b.label,
      before: existing?.before ?? 0,
      after: b.pct,
    });
  });
  return [...merged.values()];
}

function AllocationComparison({
  before,
  after,
}: {
  before: AllocationBucket[];
  after: AllocationBucket[];
}) {
  const buckets = mergeBuckets(before, after);
  if (!buckets.length) {
    return (
      <span className="text-sm text-muted-foreground">
        Allocation data is not available for these funds.
      </span>
    );
  }
  return (
    <div className="flex flex-col gap-5">
      {buckets.map((bucket, index) => {
        const color = ALLOCATION_COLORS[index % ALLOCATION_COLORS.length];
        const subs = mergeBuckets(
          before.find((b) => b.key === bucket.key)?.sub_buckets ?? [],
          after.find((b) => b.key === bucket.key)?.sub_buckets ?? []
        );
        return (
          <div key={bucket.key} className="flex flex-col gap-3">
            <AllocationRow color={color} {...bucket} />
            {subs.map((sub) => (
              <AllocationRow
                key={sub.key}
                label={sub.label}
                color={`color-mix(in srgb, ${color} 55%, transparent)`}
                before={sub.before}
                after={sub.after}
                nested
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}

function AllocationRow({
  label,
  color,
  before,
  after,
  nested,
}: {
  label: string;
  color: string;
  before: number;
  after: number;
  nested?: boolean;
}) {
  return (
    <div className="flex items-start gap-4">
      <span
        className={cn(
          "w-28 shrink-0 text-sm",
          nested ? "pl-4 text-muted-foreground" : "font-medium"
        )}
      >
        {label}
      </span>
      <BarPair before={before} after={after} color={color} />
    </div>
  );
}

function ExposureComparison({
  before,
  after,
}: {
  before: ReviewExposureSnapshot;
  after: ReviewExposureSnapshot;
}) {
  return (
    <div className="flex flex-col gap-8">
      <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-3">
        <ExposureSnapshot snapshot={before} />
        <ArrowRight className="mb-8 size-4 text-muted-foreground" />
        <ExposureSnapshot snapshot={after} highlighted />
      </div>
      <div className="flex flex-col gap-4">
        <span className="text-sm font-semibold text-muted-foreground">
          Largest fund
        </span>
        <LargestFund snapshot={before} />
        <LargestFund snapshot={after} dashed />
      </div>
    </div>
  );
}

function ExposureSnapshot({
  snapshot,
  highlighted,
}: {
  snapshot: ReviewExposureSnapshot;
  highlighted?: boolean;
}) {
  return (
    <div className="flex flex-col gap-4">
      <span className="font-semibold">{snapshot.fund_count} Funds</span>
      <div
        className={cn(
          "flex flex-col gap-3 rounded-xl p-4",
          highlighted ? "bg-card" : "border border-border"
        )}
      >
        <span className="w-fit rounded-md border border-border bg-background px-2 py-1 text-xs">
          {snapshot.amc_count} AMCs
        </span>
        <span className="text-xs">
          Top 3 AMC: {formatAsPercentage(snapshot.top_3_amc_pct, "-", 0)}
        </span>
      </div>
    </div>
  );
}

function LargestFund({
  snapshot,
  dashed,
}: {
  snapshot: ReviewExposureSnapshot;
  dashed?: boolean;
}) {
  if (!snapshot.largest_fund_name) return null;
  const pct = Number(snapshot.largest_fund_pct);
  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs text-muted-foreground">
        {snapshot.largest_fund_name}
      </span>
      <div className="flex items-center gap-4">
        <InsightBar pct={pct} color="var(--color-chart-1)" dashed={dashed} />
        <span className="w-12 text-right text-xs">{formatPct(pct)}</span>
      </div>
    </div>
  );
}
